import React, { useState, useMemo } from 'react';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import MainCard from '@/components/MainCard';
import { Tag, Space, Input, Select, Modal, Button, Card, Tooltip } from 'antd';
import DataTable from '@/components/DataTable';
import { FileCheck, Clock, CheckCircle2, XCircle, Check, X, Filter, Loader } from 'lucide-react';
import { IconLabel, SummaryTiles, TONE } from '@/components/queue/QueueKit';
import { defaultColors } from '@/themes/antd-theme';

const { TextArea } = Input;

// Predefined request types with different status colors
const REQUEST_TYPES = [
  { type: 'Finance', color: 'primary' },
  { type: 'HR', color: 'secondary' },
  { type: 'IT', color: 'info' },
  { type: 'Operations', color: 'warning' },
  { type: 'Procurement', color: 'success' },
];

const STATUS_COLORS = {
  Pending: 'warning',
  Approved: 'success',
  Rejected: 'error',
  InProgress: 'info',
};

// Initial mock data with more detailed batch processing requests
const initialRequests = [
  {
    id: "BP101",
    name: "Equipment Batch Upgrade",
    type: "Operations",
    status: "Pending",
    description: "Request to upgrade all assembly line equipment in Batch A",
    requestedBy: "Alice Brown",
    date: "2024-04-01"
  },
  {
    id: "BP102",
    name: "New Machine Approval - Batch Line 3",
    type: "Procurement",
    status: "Pending",
    description: "Approval for purchasing a new CNC machine for Batch Line 3",
    requestedBy: "Bob Green",
    date: "2024-04-05"
  },
  {
    id: "BP103",
    name: "Batch Process Redesign - Chemical Mixing",
    type: "Finance",
    status: "Pending",
    description: "Budget approval for redesigning the chemical mixing process in Batch Unit 7",
    requestedBy: "Charlie White",
    date: "2024-04-10"
  },
  {
    id: "BP104",
    name: "Safety Protocol Review - Batch Processing",
    type: "HR",
    status: "Pending",
    description: "Review and approve updated safety protocols for all batch processing units",
    requestedBy: "Diana Black",
    date: "2024-04-12"
  },
  {
    id: "BP105",
    name: "IT Infrastructure Upgrade - Batch System",
    type: "IT",
    status: "Pending",
    description: "Request for upgrading IT infrastructure supporting batch processing systems",
    requestedBy: "Eve Grey",
    date: "2024-04-15"
  },
  {
    id: "BP106",
    name: "Raw Material Procurement - Batch Cycle 5",
    type: "Procurement",
    status: "Pending",
    description: "Approval to procure raw materials for Batch Cycle 5 production run",
    requestedBy: "Frank Blue",
    date: "2024-04-18"
  },
  {
    id: "BP107",
    name: "Quality Control System - Batch Output Analysis",
    type: "Operations",
    status: "Pending",
    description: "Implement new quality control system for analyzing batch output data",
    requestedBy: "Grace Red",
    date: "2024-04-20"
  },
  {
    id: "BP108",
    name: "Training Program - New Batch Machine Operators",
    type: "HR",
    status: "Pending",
    description: "Approval for a training program for new operators of batch processing machines",
    requestedBy: "Henry Yellow",
    date: "2024-04-25"
  }
];

const ApproveRequest = () => {
  const [requests, setRequests] = useState(initialRequests);
  const [approverName, setApproverName] = useState("");
  const [nameError, setNameError] = useState(false);
  const [filterType, setFilterType] = useState("All");
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [currentRequestId, setCurrentRequestId] = useState(null);
  const [approverDescription, setApproverDescription] = useState("");

  // Status → icon + tone (display only)
  const STATUS_ICON = {
    Pending: [Clock, TONE.amber],
    Approved: [CheckCircle2, TONE.green],
    Rejected: [XCircle, TONE.red],
    InProgress: [Loader, TONE.blue],
  };

  // Define table columns
  const allColumns = [
    {
      title: "Request",
      dataIndex: "name",
      key: "name",
      width: 300,
      defaultVisible: true,
      sortType: "string",
      render: (name, r) => (
        <div style={{ lineHeight: 1.25, minWidth: 0 }}>
          <Tooltip title={name}>
            <div style={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{name}</div>
          </Tooltip>
          <div style={{ fontSize: 12, color: '#6b7280' }}>{r.id}</div>
        </div>
      ),
    },
    {
      // the request ID is the Request cell's second line; the column stays for the column picker
      title: "Request ID",
      dataIndex: "id",
      key: "id",
      width: 120,
      defaultVisible: false,
      sortType: "string",
    },
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
      width: 130,
      defaultVisible: true,
      sortType: "string",
      render: (type) => <Tag>{type}</Tag>,
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      width: 300,
      defaultVisible: true,
      ellipsis: { showTitle: false },
      render: (v) => <Tooltip title={v}><span>{v}</span></Tooltip>,
    },
    {
      title: "Requested By",
      dataIndex: "requestedBy",
      key: "requestedBy",
      width: 150,
      defaultVisible: true,
      sortType: "string",
    },
    {
      title: "Date",
      dataIndex: "date",
      key: "date",
      width: 120,
      defaultVisible: true,
      sortType: "string",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 120,
      defaultVisible: true,
      sortType: "string",
      render: (status) => {
        const [Icon, color] = STATUS_ICON[status] || [Clock, TONE.slate];
        return <IconLabel Icon={Icon} color={color} label={status === 'InProgress' ? 'In progress' : status} />;
      },
    },
    {
      title: "Actions",
      key: "actions",
      width: 80,
      align: 'center',
      fixed: 'right',
      defaultVisible: true,

      render: (_, record) => (
        <Tooltip title="Approve">
          <Button
            type="primary"
            shape="circle"
            aria-label={`Approve ${record.id}`}
            icon={<Check size={18} />}
            onClick={() => handleApproveRequest(record.id)}
            style={{
              backgroundColor: defaultColors.primary,
              borderColor: defaultColors.primary,
            }}
          />
        </Tooltip>
      ),
    },
  ];

  // Type filter (free-text search is handled by DataTable's toolbar search)
  const filteredRequests = useMemo(() => {
    return requests.filter(request => filterType === "All" || request.type === filterType);
  }, [requests, filterType]);


  // Handle Approve Request for a single row
  const handleApproveRequest = (id) => {
    setCurrentRequestId(id);
    setApproverName("");
    setNameError(false);
    setOpenConfirmDialog(true);
    setApproverDescription("");
  };

  // Confirm Approval for a single request
  const confirmApproval = () => {
    setNameError(false);

    if (!approverName.trim()) {
      setNameError(true);
      return;
    }

    if (!currentRequestId) {
      console.error("No request ID to approve.");
      return;
    }

    const requestNameToDisplay = requests.find(req => req.id === currentRequestId)?.name || 'Request';

    setRequests(prevRequests =>
      prevRequests.map(req =>
        req.id === currentRequestId
          ? { ...req, status: "Approved", approvalDescription: approverDescription }
          : req
      )
    );
    setApproverName("");
    setOpenConfirmDialog(false);
    toast.success(`${requestNameToDisplay} approved successfully!`);
    setCurrentRequestId(null);
    setApproverDescription("");
  };

  return (
    <MainCard>
      {/* Top Summary Cards */}
      <SummaryTiles
        testId="approve-request-summary"
        tiles={[
          { key: 'total', label: 'Total Requests', value: requests?.length || 0, color: TONE.blue, Icon: FileCheck },
          { key: 'pending', label: 'Pending', value: requests?.filter(r => r?.status === "Pending")?.length || 0, color: TONE.amber, Icon: Clock },
          { key: 'approved', label: 'Approved', value: requests?.filter(r => r?.status === "Approved")?.length || 0, color: TONE.green, Icon: CheckCircle2 },
          { key: 'rejected', label: 'Rejected', value: requests?.filter(r => r?.status === "Rejected")?.length || 0, color: TONE.red, Icon: XCircle },
        ]}
      />

      <Card style={{ width: '100%' }}>
        <div className="p-6">
          <h4 className="text-xl font-bold text-center mb-6" style={{ color: defaultColors.primary }}>
            Batch Approval Dashboard
          </h4>

          {/* Filters and Search */}
          <div className="flex justify-between items-center mb-4">
            <Space size="middle">
              <Select
                value={filterType}
                onChange={(value) => setFilterType(value)}
                style={{ width: 200 }}
                suffixIcon={<Filter size={16} />}
              >
                <Select.Option value="All">All Types</Select.Option>
                {REQUEST_TYPES.map(type => (
                  <Select.Option key={type.type} value={type.type}>
                    {type.type}
                  </Select.Option>
                ))}
              </Select>

            </Space>
          </div>

          {/* Request Table */}
          <DataTable
            tableKey="approveRequestTable"
            searchable
            searchPlaceholder="Search requests..."
            data={filteredRequests}
            columns={allColumns}
            bordered
            rowKey="id"
            pagination={{ pageSize: 10, showQuickJumper: true }}
            height={null}
            scrollX={1200}
          />

          {/* Confirmation Modal */}
          <Modal
            title="Approve Request"
            open={openConfirmDialog}
            onCancel={() => setOpenConfirmDialog(false)}
            footer={[
              <Button
                key="cancel"
                icon={<X size={16} />}
                onClick={() => setOpenConfirmDialog(false)}
              >
                Cancel
              </Button>,
              <Button
                key="submit"
                type="primary"
                icon={<Check size={16} />}
                onClick={confirmApproval}
                disabled={nameError || !approverName.trim()}
                style={{
                  backgroundColor: defaultColors.primary,
                  borderColor: defaultColors.primary,
                }}
              >
                Confirm Approve
              </Button>
            ]}
          >
            <div className="space-y-4">
              <p className="text-gray-600 mb-4">
                Please enter your name to approve this request.
              </p>

              <div>
                <label className="font-medium mb-1">Approver Name</label>
                <Input
                  placeholder="Enter your name"
                  value={approverName}
                  onChange={(e) => setApproverName(e.target.value)}
                  status={nameError ? 'error' : ''}
                />
                {nameError && (
                  <p className="text-sm mt-1" style={{ color: defaultColors.error }}>
                    Please enter your name to approve the request
                  </p>
                )}
              </div>

              <div>
                <label className="font-medium mb-1">Approval Description (Optional)</label>
                <TextArea
                  placeholder="Enter approval description"
                  rows={3}
                  value={approverDescription}
                  onChange={(e) => setApproverDescription(e.target.value)}
                />
              </div>

              <div className="mt-4 space-y-2">
                <p className="text-gray-600">
                  <strong>Request Description:</strong> {requests.find(req => req.id === currentRequestId)?.description}
                </p>
                <p className="text-gray-600">
                  Are you sure you want to approve request ID: <strong>{currentRequestId}</strong>?
                </p>
              </div>
            </div>
          </Modal>
        </div>
      </Card>
      <ToastContainer />
  
    </MainCard>
  );
};

export default ApproveRequest;